import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-switch-input',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchInputComponent),
      multi: true,
    },
  ],
  template: `
    <label
      class="flex items-center justify-center cursor-pointer select-none"
      [class.opacity-50]="disabled()"
    >
      <!-- Native switch: visually hidden but focusable (keyboard + screen reader). -->
      <input
        type="checkbox"
        role="switch"
        class="peer sr-only"
        [checked]="checked()"
        [disabled]="disabled()"
        (change)="toggle()"
        [id]="inputId()"
        [attr.aria-checked]="checked()"
        [attr.aria-label]="ariaLabel() || null"
      />

      <!-- Switch Track -->
      <span
        class="w-[38px] h-[22px] flex items-center rounded-full transition-all duration-200 pl-0.5 peer-focus-visible:ring-2 peer-focus-visible:ring-dark dark:peer-focus-visible:ring-white peer-focus-visible:ring-offset-1"
        [class.bg-dark]="checked()"
        [class.dark:bg-white]="checked()"
        [class.bg-default]="!checked()"
        [class.dark:bg-dark-grey]="!checked()"
      >
        <!-- Switch Thumb -->
        <span
          class="w-[18.8px] h-[18.8px] bg-white rounded-full transition-all duration-200 shadow-[0 0.8px 1.6px -0.8px rgba(0, 0, 0, 0.1), 0 0.8px 2.3px 0 rgba(0, 0, 0, 0.1)]"
          [class.translate-x-4]="checked()"
          [class.translate-x-0]="!checked()"
          [class.dark:bg-dark]="checked()"
        ></span>
      </span>
    </label>
  `,
})
export class SwitchInputComponent implements ControlValueAccessor {
  /* ===============================
   * PUBLIC INPUTS
   * =============================== */

  public inputId = input<string | null>(null);
  /** Accessible name for the switch (no visible label text is rendered). */
  public ariaLabel = input<string | null>(null);

  /* ===============================
   * INTERNAL STATE
   * =============================== */

  protected readonly checked = signal<boolean>(false);
  protected readonly disabled = signal<boolean>(false);

  private readonly cdr = inject(ChangeDetectorRef);

  private onChange: (value: boolean) => void = () => {
    /* empty */
  };
  private onTouched: () => void = () => {
    /* empty */
  };

  constructor() {
    effect(() => this.cdr.markForCheck());
  }

  /* ===============================
   * USER INTERACTION
   * =============================== */

  public toggle(): void {
    if (this.disabled()) return;

    const next = !this.checked();
    this.checked.set(next);
    this.onChange(next);
    this.onTouched();
  }

  /* ===============================
   * CONTROL VALUE ACCESSOR
   * =============================== */

  public writeValue(value: boolean): void {
    this.checked.set(!!value);
  }

  public registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
